import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  armContinuation,
  consumeContinuation,
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import { buildNavResumeSeed } from './global-ai-system-seeds';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';

/**
 * The turn-continuation firing point: cross-navigation "pending resume"
 * state, armed by an offer click into the engine store's consume-once
 * `pendingContinuation` slot, is consumed here by a location-keyed effect
 * once the router lands on the destination (so the resumed turn reads the
 * DESTINATION page context, not the page it left).
 *
 * Why an effect at all: a nav-confirm/build chip click navigates, then
 * resumes the chained task ON the destination page. React Router's
 * `navigate()` does NOT update `location` synchronously, so the resume
 * CANNOT fire in the click handler — it would run `buildTurnContext`
 * against the page the user just LEFT (wrong pageKind/taxonomy/canFilter/
 * filter-context). The store slot's atomic consume replaces the old
 * ref-per-flow shape (a ref armed-but-never-consumed could sit poisoned
 * across turns); consume-once makes a double-fire or stale resume
 * structurally unrepresentable.
 */
export function useGlobalAiTurnContinuation(args: {
  isTurnRunning: boolean;
  startAssistantTurn: (
    prompt: string,
    options?: { silent?: boolean; suppressReportOffer?: boolean }
  ) => Promise<void>;
}) {
  const location = useLocation();
  const pending = useGlobalAiEngineStore((s) => s.pendingContinuation);

  // Fire a pending resume ONCE the router has actually landed on the
  // destination. Keyed on `location.pathname` (and the armed slot) so it
  // runs after the navigation re-render — at which point `buildTurnContext`
  // reads the destination page's context — and never while a turn is
  // already running. `consumeContinuation` clears the slot atomically, so
  // the resume fires exactly once even if the effect re-runs.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on location.pathname + the armed slot; startAssistantTurn/isTurnRunning are read at fire time
  useEffect(() => {
    // `build_here` is not consumed here: it belongs to the resumed turn's
    // `create_custom_page` call (the runner consumes it).
    //
    // The turn check reads the LIVE store, not the rendered
    // `args.isTurnRunning`: a turn that began between this render and the
    // effect flush would make `startAssistantTurn` (which guards on the live
    // store) no-op AFTER we consumed the seed — destroying it, since consume
    // is once-only — and leave `build_here` armed for a turn that never runs.
    // The live read stays synchronous through to `beginTurn`, so guard and
    // start are atomic. `args.isTurnRunning` remains a dependency so the
    // effect re-runs at the turn-end edge.
    if (
      !pending ||
      pending.kind === 'build_here' ||
      selectIsTurnRunning(useGlobalAiEngineStore.getState())
    ) {
      return;
    }
    if (!globalAssistantPathsMatch(location.pathname, pending.expectedPath)) {
      return;
    }
    if (pending.kind === 'nav_resume') {
      const consumed = consumeContinuation('nav_resume');
      if (!consumed) {
        return;
      }
      void args.startAssistantTurn(
        buildNavResumeSeed({
          pageName: consumed.pageName,
          continuationPrompt: consumed.continuationPrompt,
        }),
        { silent: true }
      );
      return;
    }
    const consumed = consumeContinuation('create_page_resume');
    if (!consumed) {
      return;
    }
    // The build chip's click armed the resume; landing on the empty draft
    // converts it into the build-here signal for the turn we now start —
    // the runner's `create_custom_page` handle consumes it and runs the
    // CREATE page-gen on THIS draft instead of offering again. Arming here
    // (not at click time) keeps the single slot coherent: the resume and
    // the build signal are one continuation, handed from the location
    // effect to the runner.
    armContinuation({ kind: 'build_here' });
    // The seed was already formed at click time (full original prompt +
    // "build here" system note). Revealing the resumed turn
    // (jump-to-bottom) is centralized in the transcript pane's
    // silent-turn-start effect.
    void args.startAssistantTurn(consumed.seed, {
      silent: true,
      suppressReportOffer: true,
    });
  }, [location.pathname, args.isTurnRunning, pending]);
}
